import { useCallback, useState, type FormEvent } from 'react';
import type { RecipientGroup } from '@detect/shared';
import { recipientGroupService } from '../../services/alerting';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { RecipientsEditor } from './RecipientsEditor';
import {
  draftToRecipients,
  recipientsToDraft,
  type RecipientsDraft,
} from './recipients-draft';

export function GroupsTab() {
  const { tenantId, hasRole } = useTenant();
  const [editing, setEditing] = useState<WithId<RecipientGroup> | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      recipientGroupService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<RecipientGroup>>(fetchPage, 'all');

  const remove = async (group: WithId<RecipientGroup>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete recipient group ${group.name}?`)) return;
    setActionError(null);
    try {
      await recipientGroupService.remove(tenantId, group.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {hasRole('admin') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing('new')}
          >
            New group
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No recipient groups"
          description="Groups bundle on-call audiences for escalation chain stages."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Recipients</th>
                {hasRole('admin') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((g) => (
                <tr key={g.id}>
                  <td>{g.name}</td>
                  <td>{g.recipients.length}</td>
                  {hasRole('admin') && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setEditing(g)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(g)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {editing !== null && (
        <GroupModal
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function GroupModal({
  group,
  onClose,
  onSaved,
}: {
  group: WithId<RecipientGroup> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState(group?.name ?? '');
  const [draft, setDraft] = useState<RecipientsDraft>(
    group ? recipientsToDraft(group.recipients) : { emails: '', integrationIds: [] }
  );
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    const recipients = draftToRecipients(draft);
    if (recipients.length === 0) {
      setError(new Error('Add at least one recipient'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (group) {
        await recipientGroupService.update(tenantId, group.id, { name, recipients });
      } else {
        await recipientGroupService.create(tenantId, { name, recipients });
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={group ? `Edit ${group.name}` : 'New recipient group'} onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="group-name">Name</label>
          <input
            id="group-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <RecipientsEditor draft={draft} onChange={setDraft} />
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : group ? 'Save' : 'Create'}
        </button>
      </form>
    </Modal>
  );
}
